import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  InputGroup,
  InputGroupText,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import { PASSWORD_COMPLEXITY_REGEX } from "../../helpers/regex";

import logolight from "../../assets/images/logo-light.png";
import logodark from "../../assets/images/logo-dark.png";

import { Link, useNavigate, useParams } from "react-router-dom";
import withRouter from "../../components/Common/withRouter";
import { PLATFORM_NAME } from "../../CommonData/Data/constant";
import callApi from "../../helpers/CallApi";
import { resetPassword } from "../../helpers/API";
import { toast } from "react-toastify";

const ForgetPasswordPage = ({ t }) => {
  // Dynamic document title using translation
  document.title = `${t(
    "resetPassword.page.title"
  )} | ${PLATFORM_NAME} - React Admin & Dashboard Template`;

  const { token } = useParams();
  const navigate = useNavigate();

  // State for password visibility
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
  });

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Dynamic validation schema using translations
  const getValidationSchema = () => {
    return Yup.object({
      newPassword: Yup.string()
        .min(8, t("resetPassword.validation.passwordMinLength"))
        .matches(
          PASSWORD_COMPLEXITY_REGEX,
          t("resetPassword.validation.passwordComplexity")
        )
        .required(t("resetPassword.validation.newPasswordRequired")),

      confirmPassword: Yup.string()
        .required(t("resetPassword.validation.confirmPasswordRequired"))
        .oneOf(
          [Yup.ref("newPassword")],
          t("resetPassword.validation.passwordsMustMatch")
        ),
    });
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: getValidationSchema(),
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        await new Promise((resolve, reject) => {
          callApi({
            callback: resetPassword,
            payload: [token, { password: values.newPassword }],
            onSuccess: ({ message }) => {
              toast.success(message);
              navigate("/login");
              resolve();
            },
            onError: (error) => {
              reject(new Error(error));
            },
          });
        });
      } catch (error) {
        setFieldError(
          "newPassword",
          error.message || t("resetPassword.errors.resetFailed")
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Get password strength
  const getPasswordStrength = (password) => {
    if (!password) return { width: 0, color: "bg-danger" };

    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 12.5;
    if (/[@$!%*?&]/.test(password)) strength += 12.5;

    let color = "bg-danger";
    if (strength >= 75) color = "bg-success";
    else if (strength >= 50) color = "bg-warning";

    return { width: Math.min(strength, 100), color };
  };

  const passwordStrength = getPasswordStrength(formik.values.newPassword);

  return (
    <React.Fragment>
      <div className="bg-pattern" style={{ height: "100vh" }}>
        <div className="bg-overlay"></div>
        <div className="account-pages pt-5">
          <Container>
            <Row className="justify-content-center">
              <Col lg={6} md={8} xl={4}>
                <Card className="mt-5">
                  <CardBody className="p-4">
                    <div className="">
                      <div className="text-center">
                        <Link to="/" className="">
                          <img
                            src={logodark}
                            alt=""
                            height="24"
                            className="auth-logo logo-dark mx-auto"
                          />
                          <img
                            src={logolight}
                            alt=""
                            height="24"
                            className="auth-logo logo-light mx-auto"
                          />
                        </Link>
                      </div>
                      <h4 className="font-size-18 text-muted mt-2 text-center">
                        {t("resetPassword.heading")}
                      </h4>
                      <p className="mb-5 text-center">
                        {t("resetPassword.description", {
                          platformName: PLATFORM_NAME,
                        })}
                      </p>

                      <Form onSubmit={formik.handleSubmit}>
                        <Row>
                          <Col md={12}>
                            <div className="alert alert-warning alert-dismissible">
                              <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="alert"
                                aria-label={t("resetPassword.alert.closeLabel")}
                              ></button>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: t("resetPassword.alert.message"),
                                }}
                              />
                            </div>

                            {/* New Password */}
                            <FormGroup>
                              <Label for="newPassword">
                                {t("resetPassword.labels.newPassword")}{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <InputGroup>
                                <Input
                                  type={showPasswords.new ? "text" : "password"}
                                  id="newPassword"
                                  name="newPassword"
                                  placeholder={t(
                                    "resetPassword.placeholders.enterNewPassword"
                                  )}
                                  value={formik.values.newPassword}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  invalid={
                                    formik.touched.newPassword &&
                                    formik.errors.newPassword
                                  }
                                />
                                <InputGroupText
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    togglePasswordVisibility("new")
                                  }
                                >
                                  <i
                                    className={`ri-${
                                      showPasswords.new ? "eye-off" : "eye"
                                    }-line`}
                                  ></i>
                                </InputGroupText>
                                {formik.touched.newPassword &&
                                  formik.errors.newPassword && (
                                    <FormFeedback>
                                      {formik.errors.newPassword}
                                    </FormFeedback>
                                  )}
                              </InputGroup>

                              {/* Password strength indicator */}
                              {formik.values.newPassword && (
                                <div className="mt-2">
                                  <small className="text-muted">
                                    {t("resetPassword.passwordStrength.label")}
                                  </small>
                                  <div
                                    className="progress"
                                    style={{ height: "4px" }}
                                  >
                                    <div
                                      className={`progress-bar ${passwordStrength.color}`}
                                      style={{
                                        width: `${passwordStrength.width}%`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              )}
                            </FormGroup>

                            {/* Confirm Password */}
                            <FormGroup>
                              <Label for="confirmPassword">
                                {t("resetPassword.labels.confirmPassword")}{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <InputGroup>
                                <Input
                                  type={
                                    showPasswords.confirm ? "text" : "password"
                                  }
                                  id="confirmPassword"
                                  name="confirmPassword"
                                  placeholder={t(
                                    "resetPassword.placeholders.confirmNewPassword"
                                  )}
                                  value={formik.values.confirmPassword}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  invalid={
                                    formik.touched.confirmPassword &&
                                    formik.errors.confirmPassword
                                  }
                                />
                                <InputGroupText
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    togglePasswordVisibility("confirm")
                                  }
                                >
                                  <i
                                    className={`ri-${
                                      showPasswords.confirm ? "eye-off" : "eye"
                                    }-line`}
                                  ></i>
                                </InputGroupText>
                                {formik.touched.confirmPassword &&
                                  formik.errors.confirmPassword && (
                                    <FormFeedback>
                                      {formik.errors.confirmPassword}
                                    </FormFeedback>
                                  )}
                              </InputGroup>
                            </FormGroup>

                            <div className="d-grid mt-4">
                              <Button
                                color="primary"
                                type="submit"
                                disabled={
                                  !formik.isValid || formik.isSubmitting
                                }
                                className="waves-effect waves-light"
                              >
                                {formik.isSubmitting ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    {t(
                                      "resetPassword.buttons.resettingPassword"
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-save-line me-2"></i>
                                    {t("resetPassword.buttons.resetPassword")}
                                  </>
                                )}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </React.Fragment>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withRouter(ForgetPasswordPage));
